import type { Widget } from '@dashboard/types/config'

export const DISPLAY_DEVICES = [
  { value: 'desktop', label: 'Desktop' },
  { value: 'tablets', label: 'Tablet' },
  { value: 'phones', label: 'Phone' }
] as const

export const DISPLAY_TRIGGERS = [
  { value: 'immediate', label: 'Immediately', description: 'As soon as the page is ready' },
  { value: 'scroll', label: 'After they scroll', description: 'Once they reach a percentage of the page' },
  { value: 'time', label: 'After time on page', description: 'Give them time to look around first' },
  { value: 'exit', label: "When they're about to leave", description: 'When their cursor leaves the page' },
  { value: 'formsubmit', label: 'After a form is submitted', description: 'After any form, or only specific forms' },
  {
    value: 'barstatic',
    label: 'Pinned to the top of the page',
    description: 'Appears immediately and pushes page content down'
  },
  {
    value: 'datalayer',
    label: 'When a Google Tag Manager event fires',
    description: 'When your site pushes an event such as purchase or form_submit to the dataLayer'
  },
  { value: 'manual', label: 'Manually', description: 'Open it from a link or with JavaScript' }
] as const

/** `visibility.dataLayerEvent` for the `datalayer` trigger: the pushed event, optionally only pushes whose `key` equals `value`. */
export interface DataLayerTriggerEvent {
  event: string
  key?: string
  value?: string
}

export function describeDataLayerTriggerEvent(match: Partial<DataLayerTriggerEvent> | undefined): string {
  const event = match?.event?.trim()
  if (!event) {
    return 'No event chosen yet'
  }
  const key = match?.key?.trim()
  if (!key) {
    return `When ${event} fires`
  }
  const value = match?.value?.trim()
  return value ? `When ${event} fires with ${key} = ${value}` : `When ${event} fires with ${key} set`
}

export type DisplayTrigger = (typeof DISPLAY_TRIGGERS)[number]['value']
export type DisplayCard = 'trigger' | 'pages' | 'dismiss' | 'flow' | 'embed'

export function displayTriggers(type: Widget['type']) {
  return DISPLAY_TRIGGERS.filter((trigger) => {
    if (type === 'inline') {
      return false
    }
    if (trigger.value === 'exit') {
      return type !== 'bar' && type !== 'toaster'
    }
    if (trigger.value === 'formsubmit') {
      return type === 'modal'
    }
    if (trigger.value === 'barstatic') {
      return type === 'bar'
    }
    return true
  })
}

export function displayCards(type: Widget['type']): DisplayCard[] {
  return type === 'inline' ? ['embed', 'flow'] : ['trigger', 'pages', 'dismiss', 'flow']
}

export function displayPlacements(type: Widget['type']) {
  if (type === 'bar') {
    return [
      { value: 'top', label: 'Top' },
      { value: 'bottom', label: 'Bottom' }
    ]
  }
  if (type === 'toaster') {
    return [
      { value: 'left', label: 'Bottom left' },
      { value: 'right', label: 'Bottom right' }
    ]
  }
  return []
}

export function decodeDisplayTrigger(trigger: string | undefined): { type: string; value: number | undefined } {
  const match = trigger?.match(/^(time|scroll)_(\d+)(s|pc)$/)
  if (match?.[1] && match[2]) {
    return { type: match[1], value: Number(match[2]) }
  }
  return { type: trigger ?? 'immediate', value: undefined }
}

export function encodeDisplayTrigger(type: DisplayTrigger, value?: number): string {
  if (type === 'time') {
    return `time_${value ?? 5}s`
  }
  if (type === 'scroll') {
    return `scroll_${value ?? 50}pc`
  }
  return type
}

export const FLOW_QUESTION_CHOICES = [
  {
    value: 'unknown',
    label: "Ask questions we don't know the answer to",
    description: 'Returning visitors are not asked twice',
    summary: 'Skip known questions'
  },
  {
    value: 'every',
    label: 'Ask every question, every time',
    description: 'Starts the flow over, even when we already know the answer',
    summary: 'Ask every question'
  },
  {
    value: 'skip',
    label: 'Skip the questions',
    description: 'Go straight to the first offer or form in the flow',
    summary: 'Skip questions'
  }
] as const

export type FlowQuestionChoice = (typeof FLOW_QUESTION_CHOICES)[number]['value']

export function flowQuestionChoice(value: Widget['whenToAskNext']): FlowQuestionChoice {
  if (value === 'startover') {
    return 'every'
  }
  if (value === 'none') {
    return 'skip'
  }
  return 'unknown'
}

// Reading the three-way choice never repairs legacy pageview/session values.
// Even reselecting the displayed choice must leave the stored value untouched.
export function updateFlowQuestionChoice(
  stored: Widget['whenToAskNext'],
  choice: FlowQuestionChoice
): Widget['whenToAskNext'] {
  if (flowQuestionChoice(stored) === choice) {
    return stored
  }
  if (choice === 'every') {
    return 'startover'
  }
  if (choice === 'skip') {
    return 'none'
  }
  return 'immediately'
}
